import React from 'react'
import useStore from 'state/knovStore'
import styles from 'components/filters/filter-icon.module.scss'
import cn from 'classnames'
import streamStyles from '../PanelController/SimpleHeader/simple-header.module.scss'
import WithTooltip from 'components/shared/WithTooltip'

import UserIcon from 'components/users/UserIcon'
import { useUserHoverCard } from 'components/users/UserHoverCard'
import LinkIcon from 'components/shared/icons/LinkIcon'
import ClipIcon from 'components/shared/icons/ClipIcon'
import TeamIcon from 'components/users/TeamIcon'
import PublicIcon from 'components/shared/icons/PublicIcon'
import HomeIcon from 'components/shared/icons/HomeIcon'
import TreechatIconPng from 'components/shared/icons/TreechatIconPng'
import HodlockerIcon from 'components/shared/icons/HodlockerIcon'
import OrdinalsIcon from 'components/shared/OrdinalsIcon'
import TwetchIcon from 'components/shared/icons/TwetchIcon'
import PrivateIcon from 'components/shared/icons/PrivateIcon'
import AllIcon from 'components/shared/icons/AllIcon'
import NotiIcon from 'components/shared/icons/NotiIcon'
import StarredIcon from 'components/shared/icons/StarredIcon'
import HistoryIcon from 'components/shared/icons/HistoryIcon'
import PeopleIcon from 'components/shared/icons/PeopleIcon'
import StreamsIcon from 'components/shared/icons/StreamsIcon'
import MemesIcon from 'components/shared/icons/MemesIcon'
import { prefetchNotifications } from 'refactor/hooks/api/prefetchNotifications'
import { consumeSpaceNotificationBadge } from 'refactor/hooks/api/notificationBadgeRefetchPolicy'
import { Search, Circle, Store } from 'lucide-react'

import useStreamFilters, {
    withDepth,
    insertCenter,
    openCenter,
} from 'components/filters/useStreamFilters'
import useOpenRight from 'refactor/hooks/useOpenRight'
import usePanelContext from 'refactor/hooks/usePanelContext'
import useSearchHeaderHighlight from 'refactor/hooks/useSearchHeaderHighlight'
import { defaultPrivateStreamFilter } from 'lib/myStreamsFeature'

import { focusMiddleSearchBar, focusRightSearchBar } from 'state/imperativeApis/editorApi'
import ErrorBoundary from 'components/shared/ErrorBoundary'

const SEARCH_HEADER_HIGHLIGHT_DELAY_MS = 450

function FilterIcon(props) {
    let { panelId, filter } = usePanelContext()
    const panel = props.panel
    const filters = useStreamFilters(panelId)
    const { openRight } = useOpenRight(panelId)
    const highlightSearchHeader = useSearchHeaderHighlight()

    //console.log('FILTER ICON', filters)

    // Helper to handle filter selection
    const handleFilterSelect = async (filterObj, filterSelectFn) => {
        if (props.insertNewPanel) {
            try {
                const result = await openRight(filterObj)
                const targetPanelId = result?.panelId
                if (targetPanelId) {
                    highlightSearchHeader(targetPanelId, {
                        delay: SEARCH_HEADER_HIGHLIGHT_DELAY_MS,
                    })
                }
            } catch (error) {
                console.error('Failed to open panel for filter', error)
            }
        } else {
            filterSelectFn()
        }
    }

    const onSelectLinks = ev => {
        if (props.disable) return

        ev.preventDefault()
        ev.stopPropagation()
        handleFilterSelect({ link: true, order: 'time' }, () => filters.selectLinks())
    }

    const onSelectClips = ev => {
        if (props.disable) return

        ev.preventDefault()
        ev.stopPropagation()
        handleFilterSelect({ clip: true, order: 'time' }, () => filters.selectClips())
    }

    const onSelectPublicClips = ev => {
        if (props.disable) return

        ev.preventDefault()
        ev.stopPropagation()
        handleFilterSelect({ public_clip: true, order: 'time' }, () => filters.selectPublicClips())
    }

    const onSelectPublic = ev => {
        if (props.disable) return

        ev.preventDefault()
        ev.stopPropagation()
        handleFilterSelect({ public: true, order: filter.order || 'time' }, () =>
            filters.selectPublic(filter.order),
        )
    }

    const onSelectTreechat = ev => {
        if (props.disable) return

        ev.preventDefault()
        ev.stopPropagation()
        handleFilterSelect({ treechat: true }, () => filters.selectTreechat())
    }

    const onSelectHodlocker = ev => {
        if (props.disable) return

        ev.preventDefault()
        ev.stopPropagation()
        handleFilterSelect({ hodlocker: true }, () => filters.selectHodlocker())
    }

    const onSelectOrdinals = ev => {
        if (props.disable) return

        ev.preventDefault()
        ev.stopPropagation()
        handleFilterSelect({ ordinals: true }, () => filters.selectOrdinals())
    }

    const onSelectMarket = ev => {
        if (props.disable) return

        ev.preventDefault()
        ev.stopPropagation()
        handleFilterSelect({ market: true, market_cards: true, order: 'time' }, () =>
            filters.selectMarket(),
        )
    }

    const onSelectTwetch = ev => {
        if (props.disable) return

        ev.preventDefault()
        ev.stopPropagation()
        handleFilterSelect({ twetch: true }, () => filters.selectTwetch())
    }

    const onSelectPrivate = ev => {
        if (props.disable) return

        ev.preventDefault()
        ev.stopPropagation()
        handleFilterSelect(defaultPrivateStreamFilter(), () => filters.selectPrivate())
    }

    const onSelectTeam = (teamId, ev) => {
        if (props.disable) return

        ev.preventDefault()
        ev.stopPropagation()

        if (teamId) {
            handleFilterSelect({ team_ids: [teamId] }, () => filters.selectTeam(teamId))
        }
    }

    return (
        <div className={styles.filterIconComp}>
            {gon?.currentUser && !props.filter?.questId && props.filter?.notifications && (
                <div
                    className={styles.iconContainer}
                    onClick={
                        props.insertNewPanel
                            ? ev => {
                                  ev.preventDefault()
                                  ev.stopPropagation()
                                  handleFilterSelect({ notifications: true }, () => {})
                              }
                            : undefined
                    }
                >
                    <NotificationStream
                        disable={props.disable || props.insertNewPanel}
                        filter={props.filter}
                        panel={props.panel}
                        tip="Show Notification Stream"
                        type={props.type}
                        contextStyles={cn(props.contextStyles, props.fontIcon, props.notiIcon)}
                        showIcon
                        showNumNoti={props.showNumNoti}
                    />
                </div>
            )}

            {filters.isStarredSelected() && (
                <div className={cn(styles.iconContainer)}>
                    <StarredStream
                        disable={props.disable}
                        tip="Show Starred Threads."
                        contextStyles={cn(props.contextStyles, props.fontIcon, props.starredIcon)}
                        insertNewPanel={props.insertNewPanel}
                    />
                </div>
            )}

            {filters.isHistorySelected() && (
                <div className={cn(styles.iconContainer)}>
                    <HistoryStream
                        disable={props.disable}
                        tip="Show past actions."
                        contextStyles={cn(props.contextStyles, props.fontIcon, props.historyIcon)}
                        insertNewPanel={props.insertNewPanel}
                    />
                </div>
            )}

            {filters.isStreamsSelected() && (
                <div className={cn(styles.iconContainer)}>
                    <StreamsStream
                        disable={props.disable}
                        tip="Discover public channels"
                        contextStyles={cn(props.contextStyles, props.fontIcon, props.streamsIcon)}
                        insertNewPanel={props.insertNewPanel}
                    />
                </div>
            )}

            {filters.isMemesSelected() && (
                <div className={cn(styles.iconContainer)}>
                    <MemesStream
                        disable={props.disable}
                        tip="Discover memes"
                        contextStyles={cn(props.contextStyles, props.fontIcon, props.memesIcon)}
                        insertNewPanel={props.insertNewPanel}
                    />
                </div>
            )}

            {filters.isParticipantsSelected(filter?.participants) && (
                <div className={cn(styles.iconContainer)}>
                    <PeopleIcon
                        contextStyles={cn(props.contextStyles, props.fontIcon, props.peopleIcon)}
                    />
                </div>
            )}

            {filters.isUserSelected() && (
                <div
                    className={cn(styles.iconContainer)}
                    onClick={
                        props.insertNewPanel
                            ? ev => {
                                  ev.preventDefault()
                                  ev.stopPropagation()
                                  handleFilterSelect({ user: props.filter?.user }, () => {})
                              }
                            : undefined
                    }
                >
                    <UserStream
                        disable={props.disable || props.insertNewPanel}
                        filter={props.filter}
                        user={props.user}
                        tip="Show threads and messages you've authored."
                        type={props.type}
                        contextStyles={props.contextStyles}
                        size={'small'}
                    />
                </div>
            )}

            {filters.isLinkSelected() && (
                <div className={cn(styles.iconContainer)}>
                    <LinksStream
                        disable={props.disable}
                        onClick={onSelectLinks}
                        tip="Show Shared Url Threads"
                        type={props.type}
                        contextStyles={cn(props.contextStyles, props.fontIcon, props.linksIcon)}
                    />
                </div>
            )}

            {filters.isClipSelected() && (
                <div className={cn(styles.iconContainer)}>
                    <ClipStream
                        disable={props.disable}
                        onClick={onSelectClips}
                        tip="Show Clip Threads"
                        type={props.type}
                        contextStyles={cn(props.contextStyles, props.fontIcon, props.linksIcon)}
                    />
                </div>
            )}

            {filters.isPublicClipSelected() && (
                <div className={cn(styles.iconContainer)}>
                    <ClipStream
                        disable={props.disable}
                        onClick={onSelectPublicClips}
                        tip="Show Public Clip Threads"
                        type={props.type}
                        contextStyles={cn(props.contextStyles, props.fontIcon, props.linksIcon)}
                    />
                </div>
            )}

            {filters.isPublicSelected() &&
                (props.showPublic ? (
                    <div className={cn('public-icon-container flex-item', styles.iconContainer)}>
                        <PublicStream
                            disable={props.disable}
                            onClick={onSelectPublic}
                            tip="Show Public Stream"
                            type={props.type}
                            contextStyles={cn(props.contextStyles, props.publicIcon)}
                            insertNewPanel={props.insertNewPanel}
                        />
                    </div>
                ) : (
                    // Public in a navigation context is the Home stream, so it
                    // carries the house icon.
                    <div className={cn('public-icon-container flex-item', styles.iconContainer)}>
                        <HomeStream
                            disable={props.disable}
                            onClick={onSelectPublic}
                            tip="Show Home Stream"
                            type={props.type}
                            contextStyles={cn(props.contextStyles, props.publicIcon)}
                            insertNewPanel={props.insertNewPanel}
                        />
                    </div>
                ))}

            {filters.isFollowingSelected() && (
                <div className={cn('public-icon-container flex-item', styles.iconContainer)}>
                    <HomeStream
                        disable={props.disable}
                        tip="Show Home Stream"
                        type={props.type}
                        contextStyles={cn(props.contextStyles, props.publicIcon)}
                        insertNewPanel={props.insertNewPanel}
                    />
                </div>
            )}

            {filters.isTreechatSelected() && (
                <div className={cn('public-icon-container flex-item', styles.iconContainer)}>
                    <TreechatStream
                        disable={props.disable}
                        onClick={onSelectTreechat}
                        tip="Show Public Treechat Threads"
                        type={props.type}
                        contextStyles={cn(props.contextStyles, props.treechatIcon)}
                    />
                </div>
            )}

            {filters.isHodlockerSelected() && (
                <div className={cn('public-icon-container flex-item', styles.iconContainer)}>
                    <HodlockerStream
                        disable={props.disable}
                        onClick={onSelectHodlocker}
                        tip="Show Hodlocker Threads"
                        type={props.type}
                        contextStyles={cn(props.hodlockerIcon, props.contextStyles)}
                    />
                </div>
            )}

            {filters.isMarketSelected() && (
                <div className={cn(styles.iconContainer)}>
                    <MarketStream
                        disable={props.disable}
                        onClick={onSelectMarket}
                        tip="Show the Market"
                        contextStyles={cn(props.contextStyles, props.ordinalsIcon)}
                    />
                </div>
            )}

            {filters.isOrdinalsSelected() && !filters.isMarketSelected() && (
                <div className={cn(styles.iconContainer)}>
                    <OrdinalsStream
                        disable={props.disable}
                        onClick={onSelectOrdinals}
                        tip="Show Ordinals Threads"
                        type={props.type}
                        contextStyles={cn(props.contextStyles, props.ordinalsIcon)}
                        useCircleOrdinalsIcon={props.useCircleOrdinalsIcon}
                    />
                </div>
            )}

            {filters.isTwetchSelected() && (
                <div className={cn(styles.iconContainer)}>
                    <TwetchStream
                        disable={props.disable}
                        onClick={onSelectTwetch}
                        tip="Show Twetch Threads"
                        type={props.type}
                        contextStyles={cn(props.contextStyles, props.twetchIcon)}
                    />
                </div>
            )}

            {gon.currentUser && filters.isPrivateSelected() && (
                <div className={cn(styles.iconContainer)}>
                    <PrivateStream
                        disable={props.disable}
                        onClick={onSelectPrivate}
                        tip="Show Private Threads"
                        type={props.type}
                        contextStyles={cn(props.contextStyles, props.fontIcon, props.privateIcon)}
                        isControl={props.isControl}
                        insertNewPanel={props.insertNewPanel}
                    />
                </div>
            )}

            {gon.currentUser && filters.isAllSelected() && (
                <div
                    className={cn(styles.iconContainer)}
                    onClick={
                        props.insertNewPanel
                            ? ev => {
                                  ev.preventDefault()
                                  ev.stopPropagation()
                                  handleFilterSelect({ all: true, order: 'time' }, () => {})
                              }
                            : undefined
                    }
                >
                    <AllStream
                        disable={props.disable}
                        filter={filter}
                        panel={panel}
                        tip="Show All Stream"
                        type={props.type}
                        contextStyles={cn(props.contextStyles, props.fontIcon, props.allIcon)}
                        insertNewPanel={props.insertNewPanel}
                    />
                </div>
            )}

            {(() => {
                if (!filters.filterTeamId) return null
                const providedTeam =
                    props.team && String(props.team.id) === String(filters.filterTeamId)
                        ? props.team
                        : null
                const team =
                    providedTeam ||
                    filters.teams?.find(team => String(filters.filterTeamId) === String(team.id))
                if (!team) return null

                return (
                    <div
                        key={filters.filterTeamId}
                        className={cn(
                            'team-icon-container flex-item',
                            styles.iconContainer,
                            streamStyles.teamStreamComp,
                            props.type === 'control' ? streamStyles.controlStyles : null,
                        )}
                    >
                        <TeamIcon
                            disable={props.disable}
                            team={team}
                            onClick={onSelectTeam}
                            selectedClass={
                                filters.isTeamSelected(filters.filterTeamId) ? 'selected' : ''
                            }
                            tip={`Show ${team.name} Group`}
                            type={props.type}
                            borderStyles={props.borderStyles}
                            contextStyles={props.contextStyles}
                            initialStyles={props.initialStyles}
                            fullSize={props.fullSize}
                        />
                    </div>
                )
            })()}
        </div>
    )
}

export default React.forwardRef((props, ref) => (
    <ErrorBoundary label={'FilterIcon'}>
        <FilterIcon ref={ref} {...props} />
    </ErrorBoundary>
))

/**
 * @typedef {{
 *   user?: {id?: string | number},
 *   virtualUserIcon?: boolean,
 *   disable?: boolean,
 *   onAfterClick?: () => void,
 *   showOrdinalsBadge?: boolean,
 *   tip?: string,
 *   place?: string,
 *   contextStyles?: string,
 *   agentIcon?: boolean,
 *   showKnovigator?: boolean,
 *   size?: string,
 *   smallBadge?: boolean,
 *   [key: string]: unknown,
 * }} UserStreamProps
 */

const UserStream = React.memo(function UserStream(/** @type {UserStreamProps} */ props) {
    const { user, virtualUserIcon, disable, onAfterClick } = props
    const { panel } = usePanelContext()
    const panelId = panel?.panelId
    const filters = useStreamFilters(panelId, { append: true })
    const { openRight } = useOpenRight(panelId)

    const shouldShowOrdinalsBadge = !!props.showOrdinalsBadge && user?.id !== gon?.TWETCH_USER_ID

    const onSelectUser = React.useCallback(
        ev => {
            if (disable || user?.id === gon?.TWETCH_USER_ID) return

            ev.preventDefault()
            ev.stopPropagation()

            if (panelId) openRight(withDepth({ user: String(user.id) }))
            else filters.selectUser(String(user.id))

            // Call the callback if provided
            if (onAfterClick) {
                onAfterClick()
            }
        },
        [disable, user?.id, panelId, filters, openRight, onAfterClick],
    )

    // The hover card's Message button opens the conversation the same way the
    // avatar opens a profile: beside this panel, or centered outside panels.
    // Hovering someone inside that very conversation has nothing to open.
    const panelQuestId = panel?.filter?.questId
    const onOpenQuest = React.useCallback(
        (questId, ev) => {
            if (panelQuestId && String(panelQuestId) === String(questId)) return
            if (panelId) openRight({ questId }, { metricInputEvent: ev })
            else filters.selectNewFilter({ questId })
        },
        [panelQuestId, panelId, openRight, filters],
    )

    // Only real, clickable user avatars get the profile hover card — agent,
    // space, and virtual icons don't map to a followable user.
    const hoverCard = useUserHoverCard({
        user,
        disable:
            disable ||
            !!props.agentIcon ||
            !!virtualUserIcon ||
            !!props.showKnovigator ||
            user?.id === gon?.TWETCH_USER_ID,
        onOpenStream: onSelectUser,
        onOpenQuest,
    })

    return (
        <>
            {/* The hover card supersedes the avatar tooltip: both are hover
                affordances and they'd race each other. */}
            <WithTooltip disable={disable || hoverCard.enabled} tip={props.tip} place={props.place}>
                <div
                    // With the profile card on offer, a click toggles the card
                    // (its Profile button opens the profile); otherwise it
                    // opens the profile directly.
                    onClick={ev => {
                        if (hoverCard.openFromPress(ev)) return
                        onSelectUser(ev)
                    }}
                    {...hoverCard.triggerProps}
                >
                    <div className={styles.userStreamBadgeWrapper}>
                        <UserIcon
                            user={user}
                            contextStyles={props.contextStyles}
                            agentIcon={props.agentIcon}
                            virtualUserIcon={virtualUserIcon}
                            showKnovigator={props.showKnovigator}
                            size={props.size || 'medium'}
                        />

                        {shouldShowOrdinalsBadge && (
                            <div
                                className={cn(
                                    styles.ordinalsBadge,
                                    props.smallBadge && styles.ordinalsBadgeSmall,
                                )}
                                title="@ordinalicon"
                            >
                                <OrdinalsIcon size={props.smallBadge ? 9 : 12} />
                            </div>
                        )}
                    </div>
                </div>
            </WithTooltip>
            {hoverCard.portal}
        </>
    )
})

function LinksStream(props) {
    return (
        <WithTooltip disable={props.disable} tip={props.tip}>
            <div onClick={props.onClick}>
                <LinkIcon
                    onClick={props.onClick}
                    user={gon?.currentUser}
                    contextStyles={props.contextStyles}
                />
            </div>
        </WithTooltip>
    )
}

function ClipStream(props) {
    return (
        <WithTooltip disable={props.disable} tip={props.tip}>
            <div onClick={props.onClick}>
                <ClipIcon
                    onClick={props.onClick}
                    user={gon?.currentUser}
                    contextStyles={props.contextStyles}
                />
            </div>
        </WithTooltip>
    )
}

function HomeStream(props) {
    const { panelId } = usePanelContext()
    const { selectPublic, selectFollowing } = useStreamFilters(panelId, { append: !!props.append })
    const { openRight } = useOpenRight(panelId)

    const onSelectHome = ev => {
        if (props.disable) return

        ev.preventDefault()
        ev.stopPropagation()

        const analyticsProps = {
            from_location: 'toolbar',
            previous_stream_type: props.filter?.public
                ? 'public'
                : props.filter?.private
                ? 'private'
                : props.filter?.team_ids
                ? 'team'
                : 'other',
        }

        const isFollowingPreferred =
            gon?.currentUser?.space_options?.defaultHomeStream === 'following'

        if (props.useInsertCenter) {
            if (isFollowingPreferred) {
                openCenter({ following: true, order: 'time' })
            } else {
                openCenter({ public: true, order: 'time' })
            }
        } else if (props.insertNewPanel) {
            if (isFollowingPreferred) {
                openRight({ following: true, order: 'time' })
            } else {
                openRight({ public: true, order: 'time' })
            }
        } else {
            if (isFollowingPreferred) {
                selectFollowing()
            } else {
                selectPublic()
            }
        }

        const logHomeClick = () => window.logEv?.('CLICK_HOME_BUTTON', analyticsProps)
        if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => setTimeout(logHomeClick, 0))
        } else {
            setTimeout(logHomeClick, 0)
        }
    }

    return (
        <WithTooltip disable={props.disable} tip={props.tip}>
            <div onClick={onSelectHome}>
                <HomeIcon
                    contextStyles={props.contextStyles}
                    initialStyles={props.initialStyles}
                    selectedStyles={props.selectedStyles}
                />
            </div>
        </WithTooltip>
    )
}

function TreechatStream(props) {
    return (
        <WithTooltip disable={props.disable} tip={props.tip}>
            <div onClick={props.onClick}>
                <TreechatIconPng
                    contextStyles={props.contextStyles}
                    initialStyles={props.initialStyles}
                />
            </div>
        </WithTooltip>
    )
}

function HodlockerStream(props) {
    return (
        <WithTooltip disable={props.disable} tip={props.tip}>
            <div onClick={props.onClick}>
                <HodlockerIcon
                    contextStyles={props.contextStyles}
                    initialStyles={props.initialStyles}
                />
            </div>
        </WithTooltip>
    )
}

function OrdinalsStream(props) {
    return (
        <WithTooltip disable={props.disable} tip={props.tip}>
            <div onClick={props.onClick}>
                {true || props.useCircleOrdinalsIcon ? (
                    <Circle size={20} className={props.contextStyles} />
                ) : (
                    <OrdinalsIcon
                        contextStyles={props.contextStyles}
                        initialStyles={props.initialStyles}
                        size={40}
                    />
                )}
            </div>
        </WithTooltip>
    )
}

// The Market surface (Items grid + Activity feed) shares the ordinals icon
// slot and colour; the storefront glyph tells the two apart.
function MarketStream(props) {
    return (
        <WithTooltip disable={props.disable} tip={props.tip}>
            <div onClick={props.onClick}>
                <Store size={20} className={props.contextStyles} />
            </div>
        </WithTooltip>
    )
}

function TwetchStream(props) {
    return (
        <WithTooltip disable={props.disable} tip={props.tip}>
            <div onClick={props.onClick}>
                <TwetchIcon
                    contextStyles={props.contextStyles}
                    initialStyles={props.initialStyles}
                />
            </div>
        </WithTooltip>
    )
}

function PrivateStream(props) {
    const { panelId } = usePanelContext()
    const { selectPrivate } = useStreamFilters(panelId, { append: !!props.append })
    const { openRight } = useOpenRight(panelId)

    const onSelectPrivate = ev => {
        if (props.disable) return

        ev.preventDefault()
        ev.stopPropagation()

        window.logEv('CLICK_PRIVATE_BUTTON', {
            from_location: 'toolbar',
            previous_stream_type: props.filter?.public
                ? 'public'
                : props.filter?.private
                ? 'private'
                : props.filter?.team_ids
                ? 'team'
                : 'other',
        })

        const privateFilter = defaultPrivateStreamFilter()
        if (props.useInsertCenter) {
            openCenter(privateFilter)
        } else if (props.insertNewPanel) {
            openRight(privateFilter)
        } else {
            selectPrivate()
        }
    }

    return (
        <WithTooltip disable={props.disable} tip={props.tip}>
            <div onClick={onSelectPrivate}>
                <PrivateIcon
                    contextStyles={props.contextStyles}
                    isControl={props.isControl}
                    selectedStyles={props.selectedStyles}
                />
            </div>
        </WithTooltip>
    )
}

function AllStream(props) {
    const { panelId } = usePanelContext()
    const { selectAll } = useStreamFilters(panelId, { append: !!props.append })
    const { openRight } = useOpenRight(panelId)

    const onSelectAll = ev => {
        if (props.disable) return

        ev.preventDefault()
        ev.stopPropagation()

        if (props.insertNewPanel) {
            openRight({ all: true, order: 'time' })
        } else {
            selectAll()
        }
    }

    return (
        <WithTooltip disable={props.disable} tip={props.tip}>
            <div onClick={onSelectAll}>
                <AllIcon contextStyles={props.contextStyles} />
            </div>
        </WithTooltip>
    )
}

function StarredStream({ disable, tip, contextStyles, insertNewPanel }) {
    const { panelId } = usePanelContext()
    const { selectStarred } = useStreamFilters(panelId)
    const { openRight } = useOpenRight(panelId)

    const onSelectStarred = ev => {
        if (disable) return

        ev.preventDefault()
        ev.stopPropagation()

        if (insertNewPanel) {
            openRight({ starred: true })
        } else {
            selectStarred()
        }
    }

    return (
        <WithTooltip disable={disable} tip={tip}>
            <div onClick={onSelectStarred}>
                <StarredIcon contextStyles={contextStyles} />
            </div>
        </WithTooltip>
    )
}

function HistoryStream({ disable, tip, contextStyles, insertNewPanel }) {
    const { panelId } = usePanelContext()
    const { selectHistory } = useStreamFilters(panelId)
    const { openRight } = useOpenRight(panelId)

    const onSelectHistory = ev => {
        if (disable) return

        ev.preventDefault()
        ev.stopPropagation()

        if (insertNewPanel) {
            openRight({ history: true })
        } else {
            selectHistory()
        }
    }

    return (
        <WithTooltip disable={disable} tip={tip}>
            <div onClick={onSelectHistory}>
                <HistoryIcon contextStyles={contextStyles} />
            </div>
        </WithTooltip>
    )
}

function StreamsStream({
    disable,
    tip,
    contextStyles,
    insertNewPanel,
    iconContextStyles = null,
    selectedStyles = null,
}) {
    const { panelId } = usePanelContext()
    const { selectStreams } = useStreamFilters(panelId)
    const { openRight } = useOpenRight(panelId)

    const onSelectStreams = ev => {
        if (disable) return

        ev.preventDefault()
        ev.stopPropagation()

        if (insertNewPanel) {
            openRight({ streams: true })
        } else {
            selectStreams()
        }
    }

    return (
        <WithTooltip disable={disable} tip={tip}>
            <div onClick={onSelectStreams}>
                <StreamsIcon
                    size={20}
                    contextStyles={iconContextStyles || contextStyles}
                    selectedStyles={selectedStyles}
                />
            </div>
        </WithTooltip>
    )
}

function MemesStream({
    disable,
    tip,
    contextStyles,
    insertNewPanel,
    iconContextStyles = null,
    selectedStyles = null,
}) {
    const { panelId } = usePanelContext()
    const { selectMemes } = useStreamFilters(panelId)
    const { openRight } = useOpenRight(panelId)

    const onSelectMemes = ev => {
        if (disable) return

        ev.preventDefault()
        ev.stopPropagation()

        window.logEv('CLICK_MEMES_DISCOVERY', {
            from_location: 'toolbar',
        })

        if (insertNewPanel) {
            openRight({ memes: true })
        } else {
            selectMemes()
        }
    }

    return (
        <WithTooltip disable={disable} tip={tip}>
            <div onClick={onSelectMemes}>
                <MemesIcon
                    size={20}
                    contextStyles={iconContextStyles || contextStyles}
                    selectedStyles={selectedStyles}
                />
            </div>
        </WithTooltip>
    )
}

function NotificationStream(props) {
    const { panelId } = usePanelContext()
    const { selectNotifications } = useStreamFilters(panelId, { append: !!props.append })
    const numNoti = useStore(state => state.numNoti)

    const onSelectNotification = ev => {
        if (props.disable) return

        ev.preventDefault()
        ev.stopPropagation()

        if (props.filter?.notifications) {
            const activeSpaceId = useStore.getState().activeSpaceId
            useStore.getState().set(state => consumeSpaceNotificationBadge(state, activeSpaceId))
            if (props.panel?.triggerStreamRefresh?.({ smoothScroll: false })) return
        }

        selectNotifications()
    }

    return (
        <WithTooltip disable={props.disable} tip={props.tip} place={props.place}>
            <div
                data-testid="notifications-refresh-pill"
                onMouseEnter={prefetchNotifications}
                onTouchStart={prefetchNotifications}
                onClick={onSelectNotification}
            >
                <NotiIcon
                    showNumNoti={props.showNumNoti}
                    numNoti={numNoti}
                    canActive={props.canActive}
                    contextStyles={props.contextStyles}
                    selectedStyles={props.selectedStyles}
                    showIcon={props.showIcon}
                    absolute={props.absolute}
                    activeStyles={props.activeStyles}
                />
            </div>
        </WithTooltip>
    )
}

function PublicStream(props) {
    const { panelId } = usePanelContext()
    const { selectNewFilter } = useStreamFilters(panelId, { append: props.append })
    const { openRight } = useOpenRight(panelId)

    const onSelectPublic = ev => {
        if (props.disable) return

        ev.preventDefault()
        ev.stopPropagation()

        // Track the public button click
        window.logEv('CLICK_PUBLIC_BUTTON', {
            from_location: 'toolbar',
            current_stream_type: props.filter?.public
                ? 'public'
                : props.filter?.private
                ? 'private'
                : props.filter?.team_ids
                ? 'team'
                : 'other',
        })

        if (props.useInsertCenter) {
            openCenter({ public: true, order: 'time' })
        } else if (props.insertNewPanel) {
            openRight({ public: true, order: 'time' })
        } else {
            selectNewFilter({ public: true, order: 'time' })
        }
    }
    return (
        <WithTooltip disable={props.disable} tip={props.tip}>
            <div onClick={props.onClick || onSelectPublic}>
                <PublicIcon
                    contextStyles={props.contextStyles}
                    selectedStyles={props.selectedStyles}
                />
            </div>
        </WithTooltip>
    )
}

function SearchStream(props) {
    const panel = props.panel

    const { panelId } = usePanelContext()
    const { selectNewFilter } = useStreamFilters(panelId, { append: props.append })

    const onSelectSearch = ev => {
        if (props.disable) return

        ev.preventDefault()
        ev.stopPropagation()

        // Track the search button click
        window.logEv('CLICK_SEARCH_BUTTON', {
            from_location: 'toolbar',
            current_stream_type: props.filter?.public
                ? 'public'
                : props.filter?.private
                ? 'private'
                : props.filter?.team_ids
                ? 'team'
                : 'other',
        })

        if (props.useInsertCenter) {
            // Create a new search panel centered using openCenter
            openCenter({ search: true, query: '', all: true })
        } else {
            // Create a new search panel with search filter
            selectNewFilter({ search: true, query: '', all: true })
        }
    }

    return (
        <WithTooltip disable={props.disable} tip={props.tip} place={props.place}>
            <div onClick={onSelectSearch}>
                <div className={cn(props.contextStyles, props.selectedStyles)}>
                    <Search />
                </div>
            </div>
        </WithTooltip>
    )
}

export {
    NotificationStream,
    UserStream,
    HomeStream,
    PublicStream,
    PrivateStream,
    AllStream,
    SearchStream,
    StarredStream,
    HistoryStream,
    StreamsStream,
    MemesStream,
}
